import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { 
  useSendAgentMemberIdCardEmail, 
  useSendAgentMemberIdCardSms 
} from "@/lib/api-client-react";
import { 
  Download, 
  Mail, 
  MessageSquare, 
  Shield, 
  Loader2, 
  Send,
  ExternalLink,
  CheckCircle2
} from "lucide-react";

interface MemberIdCardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: any;
  agentUser?: any;
}

function QRCodeSVG() {
  const size = 76;
  const cells = [
    [1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1],
    [1,0,1,1,1,0,1],
    [1,0,1,0,1,0,1],
    [1,0,1,1,1,0,1],
    [1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1]
  ];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded-lg bg-white p-1.5 shadow-inner">
      {cells.map((row, r) =>
        row.map((cell, c) =>
          cell ? <rect key={`${r}-${c}`} x={c * 10 + 3} y={r * 10 + 3} width={8} height={8} rx={1} fill="#0f172a" /> : null
        )
      )}
    </svg>
  );
}

function BarcodeStripes() {
  const bars = [
    2,1,3,1,2,2,1,3,1,1,2,3,1,2,1,3,2,1,1,3,2,1,2,3,1,1,2,1,3,2,
    1,2,3,1,1,2,3,1,2,1,3,2,1,1,3,2,1,2,3,1,1,2,1,3,2,1,2,3,1
  ];
  let x = 0;
  return (
    <svg width="100%" height="32" viewBox="0 0 200 32" preserveAspectRatio="none">
      {bars.map((w, i) => {
        const rect = <rect key={i} x={x} y={0} width={w} height={32} fill="white" opacity={0.9} />;
        x += w + (i % 2 === 0 ? 1.5 : 1);
        return x < 200 ? rect : null;
      })}
    </svg>
  );
}

export default function MemberIdCardModal({
  open,
  onOpenChange,
  member,
  agentUser
}: MemberIdCardModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<"card" | "email" | "sms">("card");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [smsRecipient, setSmsRecipient] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const sendEmailMutation = useSendAgentMemberIdCardEmail();
  const sendSmsMutation = useSendAgentMemberIdCardSms();

  React.useEffect(() => {
    if (member) {
      setEmailRecipient(member.email || "");
      setSmsRecipient(member.phone || "");
      setCustomNote("");
      setActiveTab("card");
    }
  }, [member, open]);

  if (!member) return null;

  const memberName = (member.name || `${member.firstName || ''} ${member.lastName || ''}`).trim() || "VALUED MEMBER";
  const memberId = member.id || member.userId || "";
  const formattedMemberId = `DC-${memberId.substring(0, 8).toUpperCase()}`;

  const captureCardImage = async (): Promise<string | null> => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      console.error("Failed to capture card image:", err);
      return null;
    }
  };

  const handleDownloadCard = async () => {
    setIsDownloading(true);
    try {
      const dataUrl = await captureCardImage();
      if (!dataUrl) throw new Error("Could not generate image");

      const link = document.createElement("a");
      link.download = `StraightDoc_ID_Card_${memberName.replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "ID Card Downloaded",
        description: `Member ID Card saved for ${memberName}.`
      });
    } catch (err: any) {
      toast({
        title: "Download Failed",
        description: err.message || "Failed to download ID card image.",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailRecipient.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid recipient email address.",
        variant: "destructive"
      });
      return;
    }

    try {
      const cardDataUrl = await captureCardImage();
      await sendEmailMutation.mutateAsync({
        memberId,
        recipientEmail: emailRecipient.trim(),
        customNote: customNote.trim(),
        cardDataUrl: cardDataUrl || undefined
      });

      toast({
        title: "Email Sent Successfully",
        description: `Member ID card emailed to ${emailRecipient}.`
      });
      setActiveTab("card");
    } catch (err: any) {
      toast({
        title: "Email Failed",
        description: err.message || "Could not send ID card email.",
        variant: "destructive"
      });
    }
  };

  const handleSendSms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsRecipient.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid recipient phone number.",
        variant: "destructive"
      });
      return;
    }

    try {
      await sendSmsMutation.mutateAsync({
        memberId,
        recipientPhone: smsRecipient.trim(),
        customNote: customNote.trim()
      });

      toast({
        title: "SMS Sent Successfully",
        description: `Member ID card details texted to ${smsRecipient}.`
      });
      setActiveTab("card");
    } catch (err: any) {
      toast({
        title: "Text Failed",
        description: err.message || "Could not send ID card SMS.",
        variant: "destructive"
      });
    }
  };

  const cleanPhone = smsRecipient.replace(/[^0-9+]/g, '');
  const nativeSmsLink = `sms:${cleanPhone}?body=${encodeURIComponent(
    `StraightDoc Member ID Card: ${memberName} | Member ID: ${formattedMemberId}. Contact your agent or log in to view card.`
  )}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto p-6">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <Shield className="w-5 h-5 text-primary" />
            Member Access ID Card — {memberName}
          </DialogTitle>
        </DialogHeader>

        {/* Tab Selection */}
        <div className="flex items-center gap-2 p-1 bg-muted rounded-xl mb-4 text-xs font-semibold">
          <button
            onClick={() => setActiveTab("card")}
            className={`flex-1 py-2 px-3 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "card" ? "bg-white dark:bg-card text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Shield className="w-3.5 h-3.5" /> ID Card Preview
          </button>
          <button
            onClick={() => setActiveTab("email")}
            className={`flex-1 py-2 px-3 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "email" ? "bg-white dark:bg-card text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Mail className="w-3.5 h-3.5" /> Email Card
          </button>
          <button
            onClick={() => setActiveTab("sms")}
            className={`flex-1 py-2 px-3 rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeTab === "sms" ? "bg-white dark:bg-card text-primary shadow-xs" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" /> Text / SMS
          </button>
        </div>

        {activeTab === "card" && (
          <div className="space-y-5">
            {/* Visual Glassmorphic Card Element */}
            <div
              ref={cardRef}
              id="member-card-element"
              className="relative rounded-3xl p-6 overflow-hidden shadow-xl text-white select-none"
              style={{ background: "linear-gradient(135deg, #1a3a6b 0%, #2563EB 50%, #06B6D4 100%)" }}
            >
              {/* Background ambient lighting */}
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-white/10 blur-2xl pointer-events-none" />
              <div className="absolute -left-12 -bottom-12 w-52 h-52 rounded-full bg-cyan-400/20 blur-2xl pointer-events-none" />
              <div className="absolute inset-0 border border-white/20 rounded-3xl pointer-events-none" />

              <div className="relative z-10 space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-inner">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-black text-xl tracking-tight drop-shadow-md">StraightDoc</span>
                    </div>
                    <p className="text-white/80 text-[10px] tracking-wider uppercase font-semibold ml-10">Member Access Card</p>
                  </div>
                  <QRCodeSVG />
                </div>

                {/* Member Info */}
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-[0.2em] mb-1 font-semibold">Member Name</p>
                  <p className="text-white text-xl font-bold tracking-wide drop-shadow-sm truncate">{memberName}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/60 text-[10px] uppercase tracking-[0.2em] mb-1 font-semibold">Member ID</p>
                    <p className="text-white font-mono text-base font-bold tracking-wider drop-shadow-sm">{formattedMemberId}</p>
                  </div>
                  {member.dob && (
                    <div>
                      <p className="text-white/60 text-[10px] uppercase tracking-[0.2em] mb-1 font-semibold">DOB</p>
                      <p className="text-white text-sm font-semibold">{member.dob}</p>
                    </div>
                  )}
                </div>

                {/* Barcode Strip */}
                <div className="rounded-xl overflow-hidden opacity-90 pt-1">
                  <BarcodeStripes />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-2.5 pt-2">
              <Button
                onClick={handleDownloadCard}
                disabled={isDownloading}
                className="bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold gap-1.5 h-10 shadow-sm"
              >
                {isDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Download Card
              </Button>

              <Button
                onClick={() => setActiveTab("email")}
                variant="outline"
                className="rounded-xl text-xs font-semibold border-border hover:bg-primary/5 hover:text-primary gap-1.5 h-10"
              >
                <Mail className="w-3.5 h-3.5 text-primary" />
                Email Card
              </Button>

              <Button
                onClick={() => setActiveTab("sms")}
                variant="outline"
                className="rounded-xl text-xs font-semibold border-border hover:bg-cyan-500/10 hover:text-cyan-600 gap-1.5 h-10"
              >
                <MessageSquare className="w-3.5 h-3.5 text-cyan-600" />
                Text / SMS
              </Button>
            </div>
          </div>
        )}

        {activeTab === "email" && (
          <form onSubmit={handleSendEmail} className="space-y-4 pt-1">
            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-xl text-xs text-blue-800 dark:text-blue-300">
              <p className="font-semibold flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-blue-600" /> Email Member ID Card
              </p>
              <p className="mt-0.5 opacity-90">
                The member will receive a branded email containing their digital StraightDoc access card attached as a PNG image and displayed inline.
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold">Recipient Email Address *</Label>
              <Input
                type="email"
                required
                value={emailRecipient}
                onChange={e => setEmailRecipient(e.target.value)}
                placeholder="member@example.com"
                className="mt-1 text-xs rounded-xl"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Optional Agent Note</Label>
              <Textarea
                rows={3}
                value={customNote}
                onChange={e => setCustomNote(e.target.value)}
                placeholder="Add a personalized message or instructions for the member..."
                className="mt-1 text-xs rounded-xl"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setActiveTab("card")}
                className="rounded-xl text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendEmailMutation.isPending}
                className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold gap-1.5 h-10 shadow-sm"
              >
                {sendEmailMutation.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending Email...</>
                ) : (
                  <><Send className="w-3.5 h-3.5" /> Send Member Card Email</>
                )}
              </Button>
            </div>
          </form>
        )}

        {activeTab === "sms" && (
          <form onSubmit={handleSendSms} className="space-y-4 pt-1">
            <div className="p-3 bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-900 rounded-xl text-xs text-cyan-800 dark:text-cyan-300">
              <p className="font-semibold flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4 text-cyan-600" /> Text / SMS Member ID Card Details
              </p>
              <p className="mt-0.5 opacity-90">
                Send text notification containing Member ID credentials to the member or designated contact phone number.
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold">Recipient Phone Number *</Label>
              <Input
                type="tel"
                required
                value={smsRecipient}
                onChange={e => setSmsRecipient(e.target.value)}
                placeholder="(555) 000-0000"
                className="mt-1 text-xs rounded-xl"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Optional Note</Label>
              <Textarea
                rows={3}
                value={customNote}
                onChange={e => setCustomNote(e.target.value)}
                placeholder="Include additional details or reminders..."
                className="mt-1 text-xs rounded-xl"
              />
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActiveTab("card")}
                  className="rounded-xl text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sendSmsMutation.isPending}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-xs font-bold gap-1.5 h-10 shadow-sm"
                >
                  {sendSmsMutation.isPending ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Dispatching Text...</>
                  ) : (
                    <><Send className="w-3.5 h-3.5" /> Send Text Message</>
                  )}
                </Button>
              </div>

              {cleanPhone && (
                <a
                  href={nativeSmsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full text-center text-xs font-semibold text-cyan-700 dark:text-cyan-400 hover:underline flex items-center justify-center gap-1 py-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open in Mobile Messaging App
                </a>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
